import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { CardFrame, CardNote, QueryBody } from '@/components/ui/card-frame';
import { StageBars } from '@/features/time-study/components/StageVsStandardCard';
import type { TimeStudyFilters } from '@/features/time-study/types';
import { useDeviation, useReworkSummary } from '@/features/time-study/use-time-study';
import { plural } from '@/lib/plural';

import { monthRange } from '../presentation';
import { skeletonLine } from '@/components/ui/skeleton';

// Salud del estudio de tiempos (so=430, spec §3.5; mockup 01): esfuerzo real frente al
// estándar por etapa y reprocesos del mes en curso, con las mismas filas y
// formatos de Estudio de tiempos (so=420). Cada mitad aparece con su permiso.

interface Props {
  companyId: string;
  canDeviation: boolean;
  canRework: boolean;
}

function filtersFor(range: { desde: string; hasta: string }): TimeStudyFilters {
  return {
    ...range,
    tipo: null,
    area: null,
    eje: 'person',
    cumpl: 'persona',
    gran: 'day',
    tab: 'analisis',
  };
}

function Stages({ filters, companyId }: { filters: TimeStudyFilters; companyId: string }) {
  const query = useDeviation('stage', filters, { companyId, enabled: true });
  return (
    <QueryBody
      query={query}
      rows={2}
      isEmpty={(d) => d.rows.length === 0}
      emptyText="Sin trabajos terminados este mes."
    >
      {(d) => <StageBars rows={d.rows} />}
    </QueryBody>
  );
}

function Rework({ filters, companyId }: { filters: TimeStudyFilters; companyId: string }) {
  const query = useReworkSummary(filters, { companyId, enabled: true });
  const n = query.data?.totals.reworkCount;
  if (query.isError) {
    return <CardNote>No se pudieron cargar los reprocesos del mes.</CardNote>;
  }
  return (
    <p className="cb rowb dash-sub">
      {n === undefined ? (
        <span className={skeletonLine({ width: 40 })} aria-hidden="true" />
      ) : n === 0 ? (
        'Sin reprocesos este mes'
      ) : (
        <>
          <span className="badge b-red">{plural(n, 'reproceso', 'reprocesos')}</span>
          este mes
        </>
      )}
    </p>
  );
}

export function TimeHealthCard({ companyId, canDeviation, canRework }: Props): ReactElement {
  // Se recalcula en cada render: un panel abierto al cambiar de mes pasa al mes nuevo.
  const range = monthRange(new Date());
  const filters = filtersFor(range);
  const detail = `/estudio-de-tiempos?${new URLSearchParams(range).toString()}`;
  return (
    <CardFrame title="Salud del estudio de tiempos" count="mes en curso">
      {canDeviation && <Stages filters={filters} companyId={companyId} />}
      {canRework && <Rework filters={filters} companyId={companyId} />}
      <div className="dash-foot">
        <Link to={detail} className="btn ghost sm">
          Ver detalle
        </Link>
      </div>
    </CardFrame>
  );
}
